import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CreditCard, Lock, Truck, Package, Loader2, AlertCircle } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useCartStore } from '@/stores/cart-store';
import { useAuthStore } from '@/stores/auth-store';
import { formatCurrency } from '@/lib/utils';
import { useStripePromise } from '@/lib/stripe';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { SITE_SHORT_NAME } from '@/config/site';
import GiftCardRedemption from '@/components/upsell/GiftCardRedemption';
import { trackStartedCheckout } from '@/lib/integrations/storefront-events';
import { getAttribution } from '@/lib/attribution';

type Step = 'address' | 'shipping' | 'payment';

interface ShippingOption {
  id: string;
  type: 'flat_rate' | 'carrier';
  carrier: string | null;
  name: string;
  price: number;
  estimated_days_min: number | null;
  estimated_days_max: number | null;
}

interface ShippingRatesResponse {
  rates: ShippingOption[];
  free_shipping_eligible: boolean;
  order_amount: number;
  total_weight_oz: number;
}

const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || '';

// ─── Stripe Payment Form ─────────────────────────────────────────────
// Rendered inside <Elements> provider with a clientSecret
function StripePaymentForm({
  orderId,
  orderTotal,
  onSuccess,
  onError,
}: {
  orderId: string;
  orderTotal: number;
  onSuccess: (orderId: string) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation('checkout');
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMessage('');

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      const msg = error.type === 'card_error' || error.type === 'validation_error'
        ? error.message || t('payment.paymentFailed')
        : t('payment.unexpectedError');
      setErrorMessage(msg);
      onError(msg);
      setIsSubmitting(false);
    } else {
      // Payment succeeded (or requires no further action)
      onSuccess(orderId);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />
      {errorMessage && (
        <div className="flex items-center gap-2 text-sm text-red-600" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {errorMessage}
        </div>
      )}
      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={!stripe || !elements || isSubmitting}
        isLoading={isSubmitting}
      >
        {t('payment.payButton', { amount: formatCurrency(orderTotal) })}
      </Button>
    </form>
  );
}

// ─── Main Checkout ────────────────────────────────────────────────────
export default function Checkout() {
  // Tenant Stripe.js (runtime publishable key from public settings, build-time fallback).
  const stripePromise = useStripePromise();
  const { t } = useTranslation('checkout');
  const { user } = useAuthStore();
  const { items, subtotal, bundleDiscount, couponDiscount, total, clearCart } = useCartStore();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('address');

  // One "Started Checkout" per visit to this page (connected integrations only).
  useEffect(() => {
    if (items.length > 0) trackStartedCheckout(items, total());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [error, setError] = useState('');

  const [shippingAddress, setShippingAddress] = useState({
    first_name: '', last_name: '', address_line1: '', address_line2: '', city: '', state: '', postal_code: '', country: 'US', phone: '',
  });
  const [billingAddress, setBillingAddress] = useState(shippingAddress);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'paypal' | 'test'>('stripe');

  // Shipping options state
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [freeShippingEligible, setFreeShippingEligible] = useState(false);
  const [selectedShippingId, setSelectedShippingId] = useState<string>('');
  const [shippingCost, setShippingCost] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [isCalculatingTax, setIsCalculatingTax] = useState(false);

  // Stripe payment intent state
  const [stripeClientSecret, setStripeClientSecret] = useState('');
  const [stripeOrderId, setStripeOrderId] = useState('');
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold mb-4">{t('auth.signInRequired')}</h1>
          <Link to="/login"><Button>{t('auth.signInButton')}</Button></Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold mb-4">{t('auth.emptyCart')}</h1>
          <Link to="/shop"><Button>{t('auth.shopNow')}</Button></Link>
        </div>
      </div>
    );
  }

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sameAsShipping) setBillingAddress(shippingAddress);

    // All-digital carts skip the shipping step (no carrier needed). Estimate
    // tax and jump straight to payment.
    if (isAllDigital) {
      setShippingCost(0);
      setSelectedShippingId('free');
      setIsCalculatingTax(true);
      try {
        const res = await api.post<{ tax_amount: number }>('/checkout/estimate-tax', {
          shipping_address: shippingAddress,
        });
        setTaxAmount(res.tax_amount || 0);
      } catch {
        setTaxAmount(0);
      } finally {
        setIsCalculatingTax(false);
      }
      setStep('payment');
      return;
    }

    // Fetch shipping rates
    setIsLoadingRates(true);
    setError('');
    try {
      const res = await api.post<ShippingRatesResponse>('/shipping/rates/calculate', {
        zip: shippingAddress.postal_code,
        state: shippingAddress.state,
      });
      setShippingOptions(res.rates);
      setFreeShippingEligible(res.free_shipping_eligible);

      // Auto-select free shipping if eligible, otherwise select cheapest
      if (res.free_shipping_eligible) {
        setSelectedShippingId('free');
        setShippingCost(0);
      } else if (res.rates.length > 0) {
        setSelectedShippingId(res.rates[0].id);
        setShippingCost(res.rates[0].price);
      } else {
        // Nothing to select for this address — drop any selection left over
        // from a previous quote so it can't be submitted for this one.
        setSelectedShippingId('');
        setShippingCost(0);
      }

      setStep('shipping');
    } catch (err) {
      // If rate fetch fails, still allow proceeding with no rates
      setShippingOptions([]);
      setFreeShippingEligible(false);
      setSelectedShippingId('');
      setShippingCost(0);
      setStep('shipping');
    } finally {
      setIsLoadingRates(false);
    }
  };

  // One-click "Get Downloads for Free" handler for $0 all-digital carts.
  // Calls /checkout/free which creates the order, issues entitlements, sends
  // the confirmation email, and clears the cart — no Stripe, no shipping.
  const handleGetFree = async () => {
    setIsProcessing(true);
    setError('');
    try {
      await api.post<{ order_id: string; order_number: string }>(
        '/checkout/free',
        { utm_data: getAttribution() },
      );
      clearCart();
      navigate(`/account/downloads`);
    } catch (err: any) {
      setError(err?.message || t('errors.freeDownloadFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShippingSelect = (option: ShippingOption | 'free') => {
    if (option === 'free') {
      setSelectedShippingId('free');
      setShippingCost(0);
    } else {
      setSelectedShippingId(option.id);
      setShippingCost(option.price);
    }
  };

  const handleShippingSubmit = async () => {
    if (!selectedShippingId && !freeShippingEligible) {
      setError(t('shipping.selectRequired'));
      return;
    }
    // Estimate tax before showing payment step
    setIsCalculatingTax(true);
    try {
      const res = await api.post<{ tax_amount: number }>('/checkout/estimate-tax', {
        shipping_address: shippingAddress,
      });
      setTaxAmount(res.tax_amount || 0);
    } catch {
      setTaxAmount(0);
    } finally {
      setIsCalculatingTax(false);
    }
    // Reset payment intent when re-entering payment step
    setStripeClientSecret('');
    setStripeOrderId('');
    setStep('payment');
  };

  // Create Stripe payment intent when Stripe is selected on payment step
  const createStripeIntent = async () => {
    if (stripeClientSecret) return; // Already created
    setIsCreatingIntent(true);
    setError('');
    try {
      const billing = sameAsShipping ? shippingAddress : billingAddress;
      const result = await api.post<{
        order_id: string;
        order_number: string;
        client_secret: string;
        total: number;
      }>('/checkout/stripe', {
        shipping_address: shippingAddress,
        billing_address: billing,
        shipping_rate_id: selectedShippingId,
        utm_data: getAttribution(),
        sms_opt_in: smsOptIn && !!shippingAddress.phone.trim(),
      });
      setStripeClientSecret(result.client_secret);
      setStripeOrderId(result.order_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.paymentInitFailed'));
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = (orderId: string) => {
    clearCart();
    navigate(`/order-confirmation/${orderId}`);
  };

  // Test order handler (admin only)
  const handleTestOrder = async () => {
    setIsProcessing(true);
    setError('');
    try {
      const billing = sameAsShipping ? shippingAddress : billingAddress;
      const result = await api.post<{ order_id: string; order_number: string; total: number }>('/checkout/test', {
        shipping_address: shippingAddress,
        billing_address: billing,
        shipping_rate_id: selectedShippingId,
        utm_data: getAttribution(),
        sms_opt_in: smsOptIn && !!shippingAddress.phone.trim(),
      });
      clearCart();
      navigate(`/order-confirmation/${result.order_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.checkoutFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  // PayPal: create order on server
  const createPayPalOrder = async (): Promise<string> => {
    const billing = sameAsShipping ? shippingAddress : billingAddress;
    const result = await api.post<{
      order_id: string;
      order_number: string;
      paypal_order_id: string;
      total: number;
    }>('/checkout/paypal/create-order', {
      shipping_address: shippingAddress,
      billing_address: billing,
      shipping_rate_id: selectedShippingId,
      utm_data: getAttribution(),
      sms_opt_in: smsOptIn && !!shippingAddress.phone.trim(),
    });
    return result.paypal_order_id;
  };

  // PayPal: capture payment after user approves
  const capturePayPalOrder = async (data: { orderID: string }) => {
    const result = await api.post<{ success: boolean; status: string; order_id: string | null }>('/checkout/paypal/capture', {
      paypal_order_id: data.orderID,
    });
    if (result.success) {
      clearCart();
      if (result.order_id) {
        navigate(`/order-confirmation/${result.order_id}`);
      } else {
        navigate('/account/orders');
      }
    } else {
      setError(t('errors.paypalCouldNotComplete'));
    }
  };

  const updateField = (setter: typeof setShippingAddress, field: string, value: string) => {
    setter((prev) => ({ ...prev, [field]: value }));
  };

  function formatDays(min: number | null, max: number | null): string {
    if (min && max) return t('shipping.estimatedRange', { min, max });
    if (min) return t('shipping.estimatedMin', { min });
    if (max) return t('shipping.estimatedMax', { max });
    return '';
  }

  // Detect all-digital cart: every item is a digital product. Such carts skip
  // the shipping step (no carrier needed) and, at $0 total, can be fulfilled
  // via /checkout/free with a single click — no Stripe, no shipping address.
  const isAllDigital = items.length > 0 && items.every((i: any) => i.is_digital === true);
  const isFreeAllDigital = isAllDigital && total() === 0;
  const orderTotal = isAllDigital ? total() + taxAmount : total() + shippingCost + taxAmount;

  const steps: { key: Step; label: string }[] = isAllDigital
    ? [
        { key: 'address', label: t('steps.billing') },
        { key: 'payment', label: t('steps.payment') },
      ]
    : [
        { key: 'address', label: t('steps.address') },
        { key: 'shipping', label: t('steps.shipping') },
        { key: 'payment', label: t('steps.payment') },
      ];

  // For all-digital carts, skip the shipping-rates step in handleAddressSubmit.
  // (We bypass shipping rate fetch by overriding setStep below in the form's onSubmit.)

  return (
    <div className="min-h-screen bg-surface-soft">
      <header className="bg-surface border-b border-line py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/cart" className="flex items-center gap-2 text-sm text-ink-soft hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> {t('header.backToCart')}
          </Link>
          <span className="font-display font-bold text-sage-800">{SITE_SHORT_NAME}</span>
          <div className="flex items-center gap-1 text-sm text-ink-muted">
            <Lock className="h-3 w-3" /> {t('header.secureCheckout')}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Steps indicator */}
        <nav className="flex items-center gap-2 text-sm mb-8" aria-label={t('header.stepsAriaLabel')}>
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2">
              {i > 0 && <span className="text-ink-muted">/</span>}
              <span className={step === s.key ? 'font-semibold text-sage-700' : 'text-ink-muted'}>
                {i + 1}. {s.label}
              </span>
            </div>
          ))}
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            {/* ─── Step: Address ─────────────────────────────────── */}
            {step === 'address' && (
              <form onSubmit={handleAddressSubmit} className="space-y-6">
                {isAllDigital && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
                    <Lock className="h-5 w-5 text-emerald-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900">{t('digital.noticeHeading')}</p>
                      <p className="text-sm text-emerald-700 mt-0.5">
                        {t('digital.noticeDescription')}
                      </p>
                    </div>
                  </div>
                )}

                {isFreeAllDigital && (
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <h2 className="text-lg font-semibold mb-2">{t('digital.freeHeading')}</h2>
                    <p className="text-sm text-ink-soft mb-4">
                      {t('digital.freeDescription')}
                    </p>
                    <Button
                      type="button"
                      size="lg"
                      className="w-full"
                      onClick={handleGetFree}
                      isLoading={isProcessing}
                    >
                      {t('digital.freeButton')}
                    </Button>
                    {error && <p className="text-red-600 text-sm mt-3" role="alert">{error}</p>}
                  </div>
                )}

                <div className="bg-surface rounded-lg border border-line p-6">
                  <h2 className="text-lg font-semibold mb-4">{isAllDigital ? t('address.billingHeading') : t('address.shippingHeading')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label={t('address.firstName')} value={shippingAddress.first_name} onChange={(e) => updateField(setShippingAddress, 'first_name', e.target.value)} required />
                    <Input label={t('address.lastName')} value={shippingAddress.last_name} onChange={(e) => updateField(setShippingAddress, 'last_name', e.target.value)} required />
                    <div className="sm:col-span-2">
                      <Input label={t('address.address')} value={shippingAddress.address_line1} onChange={(e) => updateField(setShippingAddress, 'address_line1', e.target.value)} required />
                    </div>
                    <div className="sm:col-span-2">
                      <Input label={t('address.addressLine2')} value={shippingAddress.address_line2} onChange={(e) => updateField(setShippingAddress, 'address_line2', e.target.value)} />
                    </div>
                    <Input label={t('address.city')} value={shippingAddress.city} onChange={(e) => updateField(setShippingAddress, 'city', e.target.value)} required />
                    <Input label={t('address.state')} value={shippingAddress.state} onChange={(e) => updateField(setShippingAddress, 'state', e.target.value)} required />
                    <Input label={t('address.zip')} value={shippingAddress.postal_code} onChange={(e) => updateField(setShippingAddress, 'postal_code', e.target.value)} required />
                    <Input label={t('address.phone')} type="tel" value={shippingAddress.phone} onChange={(e) => updateField(setShippingAddress, 'phone', e.target.value)} />
                  </div>

                  <label className="flex items-start gap-2 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={smsOptIn}
                      disabled={!shippingAddress.phone.trim()}
                      onChange={(e) => setSmsOptIn(e.target.checked)}
                      className="mt-1 rounded border-line-strong"
                    />
                    <span className="text-sm text-ink-soft">
                      {t('address.smsOptIn')}
                      <span className="block text-xs text-ink-muted mt-0.5">{t('address.smsDisclosure', { site: SITE_SHORT_NAME })}</span>
                    </span>
                  </label>

                  {!isAllDigital && (
                    <label className="flex items-center gap-2 mt-4 cursor-pointer">
                      <input type="checkbox" checked={sameAsShipping} onChange={(e) => setSameAsShipping(e.target.checked)} className="rounded border-line-strong" />
                      <span className="text-sm text-ink-soft">{t('address.billingSameAsShipping')}</span>
                    </label>
                  )}
                </div>

                {!isAllDigital && !sameAsShipping && (
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <h2 className="text-lg font-semibold mb-4">{t('address.billingHeading')}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label={t('address.firstName')} value={billingAddress.first_name} onChange={(e) => updateField(setBillingAddress, 'first_name', e.target.value)} required />
                      <Input label={t('address.lastName')} value={billingAddress.last_name} onChange={(e) => updateField(setBillingAddress, 'last_name', e.target.value)} required />
                      <div className="sm:col-span-2">
                        <Input label={t('address.address')} value={billingAddress.address_line1} onChange={(e) => updateField(setBillingAddress, 'address_line1', e.target.value)} required />
                      </div>
                      <div className="sm:col-span-2">
                        <Input label={t('address.addressLine2')} value={billingAddress.address_line2} onChange={(e) => updateField(setBillingAddress, 'address_line2', e.target.value)} />
                      </div>
                      <Input label={t('address.city')} value={billingAddress.city} onChange={(e) => updateField(setBillingAddress, 'city', e.target.value)} required />
                      <Input label={t('address.state')} value={billingAddress.state} onChange={(e) => updateField(setBillingAddress, 'state', e.target.value)} required />
                      <Input label={t('address.zip')} value={billingAddress.postal_code} onChange={(e) => updateField(setBillingAddress, 'postal_code', e.target.value)} required />
                      <Input label={t('address.phone')} type="tel" value={billingAddress.phone} onChange={(e) => updateField(setBillingAddress, 'phone', e.target.value)} />
                    </div>
                  </div>
                )}

                <Button type="submit" size="lg" className="w-full" isLoading={isLoadingRates || isCalculatingTax}>
                  {isAllDigital ? t('address.continueToPayment') : t('address.continueToShipping')}
                </Button>
              </form>
            )}

            {/* ─── Step: Shipping ────────────────────────────────── */}
            {step === 'shipping' && (
              <div className="space-y-6">
                <div className="bg-surface rounded-lg border border-line p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Truck className="h-5 w-5 text-ink-soft" aria-hidden="true" />
                    {t('shipping.methodHeading')}
                  </h2>

                  <div className="space-y-3">
                    {/* Free shipping option */}
                    {freeShippingEligible && (
                      <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-success-50 border-success-200 bg-success-50/50">
                        <input
                          type="radio"
                          name="shipping"
                          value="free"
                          checked={selectedShippingId === 'free'}
                          onChange={() => handleShippingSelect('free')}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-success-800">{t('shipping.freeOptionLabel')}</span>
                            <span className="text-sm font-semibold text-success-700">{t('shipping.freeOptionPrice')}</span>
                          </div>
                          <p className="text-xs text-success-600 mt-0.5">{t('shipping.freeOptionTagline')}</p>
                        </div>
                      </label>
                    )}

                    {/* Carrier rates */}
                    {shippingOptions.filter(o => o.type === 'carrier').length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-ink-muted uppercase tracking-wider mb-2">{t('shipping.liveCarrierRates')}</p>
                        {shippingOptions.filter(o => o.type === 'carrier').map(option => (
                          <label key={option.id} className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-surface-soft mb-2 ${selectedShippingId === option.id ? 'border-sage-500 bg-sage-50/30' : 'border-line'}`}>
                            <input
                              type="radio"
                              name="shipping"
                              value={option.id}
                              checked={selectedShippingId === option.id}
                              onChange={() => handleShippingSelect(option)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-sm font-medium">{option.name}</span>
                                  {option.carrier && (
                                    <span className="ml-2 text-xs bg-surface-muted text-ink-soft px-1.5 py-0.5 rounded uppercase">{option.carrier}</span>
                                  )}
                                </div>
                                <span className="text-sm font-semibold">{formatCurrency(option.price)}</span>
                              </div>
                              {(option.estimated_days_min || option.estimated_days_max) && (
                                <p className="text-xs text-ink-muted mt-0.5">{formatDays(option.estimated_days_min, option.estimated_days_max)}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Flat rate options */}
                    {shippingOptions.filter(o => o.type === 'flat_rate').length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-ink-muted uppercase tracking-wider mb-2">{t('shipping.standardRates')}</p>
                        {shippingOptions.filter(o => o.type === 'flat_rate').map(option => (
                          <label key={option.id} className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-surface-soft mb-2 ${selectedShippingId === option.id ? 'border-sage-500 bg-sage-50/30' : 'border-line'}`}>
                            <input
                              type="radio"
                              name="shipping"
                              value={option.id}
                              checked={selectedShippingId === option.id}
                              onChange={() => handleShippingSelect(option)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{option.name}</span>
                                <span className="text-sm font-semibold">{formatCurrency(option.price)}</span>
                              </div>
                              {(option.estimated_days_min || option.estimated_days_max) && (
                                <p className="text-xs text-ink-muted mt-0.5">{formatDays(option.estimated_days_min, option.estimated_days_max)}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* No options available */}
                    {shippingOptions.length === 0 && !freeShippingEligible && (
                      <div className="text-center py-6 text-ink-muted">
                        <Package className="h-8 w-8 mx-auto mb-2 text-ink-muted" aria-hidden="true" />
                        <p className="text-sm">{t('shipping.noRatesHeading')}</p>
                        <p className="text-xs mt-1">{t('shipping.noRatesHelp')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep('address')}>{t('shipping.back')}</Button>
                  <Button
                    size="lg"
                    className="flex-1"
                    onClick={handleShippingSubmit}
                    disabled={!selectedShippingId && !freeShippingEligible}
                  >
                    {t('shipping.continue')}
                  </Button>
                </div>
              </div>
            )}

            {/* ─── Step: Payment ─────────────────────────────────── */}
            {step === 'payment' && (
              <div className="space-y-6">
                <GiftCardRedemption />

                {/* Email confirmation notice */}
                {user?.email && (
                  <div className="bg-surface-soft rounded-lg border border-line px-4 py-3 text-sm text-ink-soft">
                    {t('payment.emailNoticePrefix')}{' '}
                    <span className="font-medium text-ink">{user.email}</span>
                  </div>
                )}

                {/* Payment method selector */}
                <div className="bg-surface rounded-lg border border-line p-6">
                  <h2 className="text-lg font-semibold mb-4">{t('payment.heading')}</h2>
                  <div className="space-y-3">
                    {stripePromise && (
                      <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-surface-soft ${paymentMethod === 'stripe' ? 'border-sage-500 bg-sage-50/30' : 'border-line'}`}>
                        <input type="radio" name="payment" value="stripe" checked={paymentMethod === 'stripe'} onChange={() => setPaymentMethod('stripe')} />
                        <CreditCard className="h-5 w-5 text-ink-soft" aria-hidden="true" />
                        <span className="text-sm font-medium">{t('payment.card')}</span>
                      </label>
                    )}
                    {PAYPAL_CLIENT_ID && (
                      <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-surface-soft ${paymentMethod === 'paypal' ? 'border-sage-500 bg-sage-50/30' : 'border-line'}`}>
                        <input type="radio" name="payment" value="paypal" checked={paymentMethod === 'paypal'} onChange={() => setPaymentMethod('paypal')} />
                        <span className="text-sm font-bold text-blue-700">{t('payment.paypal')}</span>
                      </label>
                    )}
                    {user?.role === 'admin' && (
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-amber-50 border-amber-200 bg-amber-50/30">
                        <input type="radio" name="payment" value="test" checked={paymentMethod === 'test'} onChange={() => setPaymentMethod('test')} />
                        <div>
                          <span className="text-sm font-medium text-amber-800">{t('payment.testOrderLabel')}</span>
                          <p className="text-xs text-amber-600">{t('payment.testOrderDescription')}</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* ─── Stripe Card Form ──────────────────────────── */}
                {paymentMethod === 'stripe' && stripePromise && (
                  <StripeCheckoutSection
                    stripeClientSecret={stripeClientSecret}
                    isCreatingIntent={isCreatingIntent}
                    orderId={stripeOrderId}
                    orderTotal={orderTotal}
                    onCreateIntent={createStripeIntent}
                    onSuccess={handlePaymentSuccess}
                    onError={(msg) => setError(msg)}
                  />
                )}

                {/* ─── PayPal Buttons ────────────────────────────── */}
                {paymentMethod === 'paypal' && PAYPAL_CLIENT_ID && (
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                        createOrder={async () => {
                          try {
                            return await createPayPalOrder();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : t('errors.paypalCreateOrderFailed'));
                            throw err;
                          }
                        }}
                        onApprove={async (data) => {
                          try {
                            await capturePayPalOrder(data);
                          } catch (err) {
                            setError(err instanceof Error ? err.message : t('errors.paypalCaptureFailed'));
                          }
                        }}
                        onCancel={() => {
                          setError(t('errors.paypalCancelled'));
                        }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          setError(t('errors.paypalGenericError'));
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>
                )}

                {/* ─── Test Order (Admin) ────────────────────────── */}
                {paymentMethod === 'test' && (
                  <Button size="lg" className="w-full" onClick={handleTestOrder} isLoading={isProcessing}>
                    {t('payment.testOrderButton', { amount: formatCurrency(orderTotal) })}
                  </Button>
                )}

                {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setStep(isAllDigital ? 'address' : 'shipping'); setStripeClientSecret(''); setStripeOrderId(''); setError(''); }}>
                    {t('payment.back')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order summary sidebar */}
          <aside className="lg:w-72" aria-label={t('summary.ariaLabel')}>
            <div className="bg-surface rounded-lg border border-line p-4 sticky top-4">
              <h3 className="font-semibold text-sm mb-3">{t('summary.headingWithCount', { count: items.length })}</h3>
              <div className="space-y-2 text-sm max-h-48 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between">
                    <span className="text-ink-soft truncate mr-2">{t('summary.itemLine', { name: item.product_name, quantity: item.quantity })}</span>
                    <span className="font-medium shrink-0">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-line mt-3 pt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>{t('summary.subtotal')}</span><span>{formatCurrency(subtotal())}</span></div>
                {bundleDiscount() > 0 && <div className="flex justify-between text-success-700"><span>{t('summary.bundleSavings')}</span><span>-{formatCurrency(bundleDiscount())}</span></div>}
                {couponDiscount > 0 && <div className="flex justify-between text-success-700"><span>{t('summary.discount')}</span><span>-{formatCurrency(couponDiscount)}</span></div>}
                <div className="flex justify-between">
                  <span>{t('summary.shipping')}</span>
                  <span>{step === 'address' ? t('summary.calculatedNext') : shippingCost === 0 ? t('summary.free') : formatCurrency(shippingCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('summary.tax')}</span>
                  <span>{step === 'payment' ? (isCalculatingTax ? t('summary.calculating') : formatCurrency(taxAmount)) : t('summary.calculatedAtCheckout')}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-1">
                  <span>{t('summary.total')}</span>
                  <span>{step === 'address' ? formatCurrency(total()) : formatCurrency(orderTotal)}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

// ─── Stripe Checkout Section ─────────────────────────────────────────
// Manages the intent creation → Elements rendering lifecycle
function StripeCheckoutSection({
  stripeClientSecret,
  isCreatingIntent,
  orderId,
  orderTotal,
  onCreateIntent,
  onSuccess,
  onError,
}: {
  stripeClientSecret: string;
  isCreatingIntent: boolean;
  orderId: string;
  orderTotal: number;
  onCreateIntent: () => void;
  onSuccess: (orderId: string) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation('checkout');
  const stripePromise = useStripePromise();
  // Auto-create payment intent when this section mounts — once. A failed
  // attempt leaves the secret empty and flips isCreatingIntent back to false,
  // so without this guard the effect would re-fire forever, and each POST can
  // create a pending order. Re-entering the payment step remounts and retries.
  const intentRequested = useRef(false);
  useEffect(() => {
    if (!stripeClientSecret && !isCreatingIntent && !intentRequested.current) {
      intentRequested.current = true;
      onCreateIntent();
    }
  }, [stripeClientSecret, isCreatingIntent, onCreateIntent]);

  if (isCreatingIntent) {
    return (
      <div className="bg-surface rounded-lg border border-line p-6 flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-sage-600 mr-2" />
        <span className="text-ink-soft text-sm">{t('payment.preparing')}</span>
      </div>
    );
  }

  if (!stripeClientSecret) {
    return null;
  }

  return (
    <div className="bg-surface rounded-lg border border-line p-6">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: stripeClientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#16a34a',
              borderRadius: '8px',
            },
          },
        }}
      >
        <StripePaymentForm
          orderId={orderId}
          orderTotal={orderTotal}
          onSuccess={onSuccess}
          onError={onError}
        />
      </Elements>
    </div>
  );
}
